/**
 * Last-active-context mapping helpers — CCM Handoff #1, Surface A
 * (consumed by `LastActiveContextSync`).
 *
 * Pure, React-free, unit-testable seam between the lexide selection store
 * and the lexserver `/api/lx/context` record. Kept separate from the sync
 * component so the scope/id-chain mapping (the part that MUST agree with the
 * server's strict `PutContextStateSchema`) is testable in node without a
 * React mount.
 */

import type { ActiveSelection } from "@/stores/useActiveContextStore";
import type { PutContextBody, UserContextRecord } from "@/lib/lexserver-client";

/** A scope + its (partial) id-chain — the shape both a PUT body and a server
 *  record share for dedup-keying. */
interface ScopeIds {
  scope: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
}

/**
 * Map the active selection (+ the derived session key) to the server's strict
 * `{scope, prefix-ids}` PUT body. Mirrors `PutContextStateSchema`: each scope
 * carries EXACTLY its chain prefix (no deeper ids), so the server never 400s
 * a body we built. A null/half-formed selection collapses to `global`
 * (defensive — `global` is always valid).
 */
export function selectionToContextBody(
  selection: ActiveSelection | null,
  sessionKey: string,
): PutContextBody {
  if (selection) {
    const { scope, clientId, matterId, caseId } = selection;
    if (scope === "case" && clientId && matterId && caseId) {
      return { scope: "case", clientId, matterId, caseId, sessionKey, updatedFrom: "lexide" };
    }
    if (scope === "matter" && clientId && matterId) {
      return { scope: "matter", clientId, matterId, sessionKey, updatedFrom: "lexide" };
    }
    if (scope === "client" && clientId) {
      return { scope: "client", clientId, sessionKey, updatedFrom: "lexide" };
    }
  }
  return { scope: "global", sessionKey, updatedFrom: "lexide" };
}

/**
 * A stable dedup key for a context (scope + its id-chain). Excludes the
 * sessionKey (derived from scope+ids → redundant) and `updatedFrom`/`updatedAt`
 * (volatile/constant) so the WRITE path only PUTs when the LOGICAL context
 * changes, not on every route-churn re-render. Works for both a PUT body and a
 * server record (both carry `scope` + the prefix ids).
 */
export function contextKey(x: ScopeIds): string {
  return [x.scope, x.clientId ?? "", x.matterId ?? "", x.caseId ?? ""].join("|");
}

/**
 * Is this server record a real, resumable CASE landing? A fresh user gets the
 * synthesised `global` default with `updatedAt` = epoch (`new Date(0)`), which
 * must NOT trigger a resume; only a persisted `case` row with a full triple
 * does. The type guard narrows the case ids to `string` for the caller.
 */
export function isResumableCaseRecord(
  ctx: UserContextRecord | null,
): ctx is UserContextRecord & { clientId: string; matterId: string; caseId: string } {
  return (
    ctx != null &&
    ctx.scope === "case" &&
    !!ctx.clientId &&
    !!ctx.matterId &&
    !!ctx.caseId &&
    new Date(ctx.updatedAt).getTime() > 0
  );
}
